import React, { useState } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { EyeIcon, CalendarIcon, UserIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { PhotoWithDetails } from '../types';
import PhotoModal from './PhotoModal';
import PlaceholderImage from './PlaceholderImage';
import { useAuth } from '../contexts/AuthContext';

interface PhotoGridProps {
  photos: PhotoWithDetails[];
  loading?: boolean;
}

const PhotoGrid: React.FC<PhotoGridProps> = ({ photos, loading = false }) => {
  const { token } = useAuth();
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoWithDetails | null>(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(-1);
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());

  const getPhotoUrl = (photoId: number) => {
    return `/api/photos/image/${photoId}?token=${encodeURIComponent(token || '')}`;
  };

  const handleImageError = (photoId: number) => {
    setBrokenImages(prev => new Set(Array.from(prev).concat(photoId)));
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="bg-gray-200 animate-pulse rounded-lg aspect-square" />
        ))}
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-12">
        <BuildingStorefrontIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Фотографии не найдены</h3>
        <p className="text-gray-500">Попробуйте изменить фильтры поиска</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className="bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-200 overflow-hidden transition-all duration-300 cursor-pointer group hover:-translate-y-1"
            onClick={() => {
              setSelectedPhoto(photo);
              setSelectedPhotoIndex(index);
            }}
          >
            {/* Photo */}
            <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
              {brokenImages.has(photo.id) ? (
                <PlaceholderImage
                  className="w-full h-full"
                  text="Изображение недоступно"
                />
              ) : (
                <>
                  <img
                    src={getPhotoUrl(photo.id)}
                    alt={`Фото от ${photo.agent?.name || 'Неизвестно'}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                    onError={() => handleImageError(photo.id)}
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="bg-white/90 backdrop-blur-sm rounded-full p-2">
                            <EyeIcon className="h-5 w-5 text-gray-800" />
                          </div>
                          <span className="text-white text-sm font-semibold">Просмотреть</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Info */}
            <div className="p-4 space-y-3 bg-gradient-to-b from-white to-gray-50">
              {/* Date */}
              <div className="flex items-center justify-between">
                <div className="flex items-center text-xs text-gray-600">
                  <div className="bg-indigo-50 p-1 rounded mr-2">
                    <CalendarIcon className="h-3.5 w-3.5 text-indigo-600" />
                  </div>
                  <span className="font-medium">{format(new Date(photo.photoDate), 'dd.MM.yyyy', { locale: ru })}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {format(new Date(photo.photoDate), 'HH:mm', { locale: ru })}
                </span>
              </div>

              {/* Agent */}
              {photo.agent && (
                <div className="flex items-center">
                  <div className="bg-blue-50 p-1 rounded mr-2">
                    <UserIcon className="h-3.5 w-3.5 text-blue-600" />
                  </div>
                  <span className="text-sm text-gray-700 font-medium truncate">{photo.agent.name}</span>
                </div>
              )}

              {/* Client */}
              {photo.client && (
                <div className="flex items-center">
                  <div className="bg-green-50 p-1 rounded mr-2">
                    <BuildingStorefrontIcon className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-600 truncate">{photo.client.name}</span>
                </div>
              )}

              {/* Category */}
              {photo.category && (
                <div className="pt-2 border-t border-gray-100">
                  <span className="inline-flex items-center bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 text-xs font-semibold px-2.5 py-1 rounded-lg border border-indigo-200">
                    {photo.category.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        <PhotoModal
          photo={selectedPhoto}
          photos={photos}
          currentIndex={selectedPhotoIndex}
          onClose={() => {
            setSelectedPhoto(null);
            setSelectedPhotoIndex(-1);
          }}
          onNavigate={(newIndex) => {
            setSelectedPhoto(photos[newIndex]);
            setSelectedPhotoIndex(newIndex);
          }}
        />
      )}
    </>
  );
};

export default PhotoGrid;